/* -------------------------------------------
   HeaderMegamenu
-------------------------------------------- */
class HeaderMegamenu extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  normalizeId(id) {
    return id?.replace(/\s+/g, '').trim() || '';
  }

  init() {
    this.querySelectorAll('details').forEach(detail => {
      const menuId = this.normalizeId(detail.dataset.menuId);
      const has2menu = detail.dataset.has_2_menu === "true";

      const menuContents = [...detail.querySelectorAll('.mega-menu__content')];
      const desktopMenus = [...detail.querySelectorAll('.megamenu-desktop')];

      let matched = null;

      const fallbackMenus = menuContents.filter(mc => !mc.dataset.itemId);

      menuContents.forEach(mc => {
        let itemId = this.normalizeId(mc.dataset.itemId);
        const parent = mc.closest('.megamenu-desktop');

        if (!itemId || !menuId) return;

        if (itemId.includes('-') && !menuId.includes('-') && !has2menu) {
          itemId = itemId.split('-').pop();
        }

        const isMatch = itemId === menuId;

        mc.classList.toggle('hidden', !isMatch);
        parent?.classList.toggle('hidden', !isMatch);
        parent?.classList.toggle('matched-menu', isMatch);

        if (isMatch) matched = { mc, parent };
      });

      /* Fallback handling */
      if (fallbackMenus.length) {
        const fallback = fallbackMenus[0];

        if (matched) {
          matched.mc.classList.add('hidden');
          matched.parent?.classList.remove('matched-menu');
          matched.parent?.classList.add('hidden');

          fallback.classList.remove('hidden');
          fallback.classList.add('matched-menu');

          try {
            fallback.style.cssText = matched.mc.style.cssText;
          } catch (_) {}
        } else {
          fallback.classList.remove('hidden');
          fallback.classList.add('matched-menu');
        }

        fallbackMenus.slice(1).forEach(f => f.classList.add('hidden'));
      }

      const allHidden =
        desktopMenus.every(d => d.classList.contains('hidden')) &&
        fallbackMenus.every(f => f.classList.contains('hidden'));

      detail.classList.toggle('all-hide', allHidden);
    });
  }
}

customElements.define('header-megamenu', HeaderMegamenu);


/* -------------------------------------------
   NavMegamenu
-------------------------------------------- */
class NavMegamenu extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.animations = new Map();
    this.hoverTimeout = null;

    this.init();
    this.showLinks();
  }

  /* ---------- HELPERS ---------- */

  getMenu(detail) {
    return (
      detail.querySelector('.megamenu-desktop.matched-menu') ||
      detail.querySelector('.mega-menu__content.matched-menu')
    );
  }

  shouldBeTransparent() {
    return window.scrollY <= 150;
  }

  setHeaderTransparentIfAllowed() {
    if (
      this.header?.dataset.headerVersion === 'transparent' &&
      this.shouldBeTransparent()
    ) {
      this.header.classList.add('transparent');
    }
  }

  removeHeaderTransparency() {
    if (this.header?.dataset.headerVersion === 'transparent') {
      this.header.classList.remove('transparent');
    }
  }

  /* ---------- SHOW / HIDE LINKS ---------- */

  showLinks() {
    this.querySelectorAll('header-megamenu details').forEach(detail => {
      const li = detail.closest('li');
      const anchor = li?.querySelector('a.menu-item');
      const hasMenu = !!this.getMenu(detail);

      anchor?.classList.toggle('hidden', hasMenu);
      detail.classList.toggle('hidden', !hasMenu);
    });
  }

  /* ---------- INIT ---------- */

  init() {
    const details = this.querySelectorAll('details');

    details.forEach(detail => {
      const summary = detail.querySelector('summary');
      summary?.addEventListener('click', e => {
        if (!e.target.closest('a')) e.preventDefault();
      });

      detail.addEventListener('mouseenter', () => {
        this.open(detail, details);
        document.body.classList.add('megamenu-open');
      });

      detail.addEventListener('mouseleave', e => {
        const menu = this.getMenu(detail);
        if (menu && menu.contains(e.relatedTarget)) return;

        this.closeDelayed(detail);
        document.body.classList.remove('megamenu-open');
      });
    });
  }

  /* ---------- INTERACTIONS ---------- */

  open(detail, all) {
    clearTimeout(this.hoverTimeout);
    this.cancel(detail);

    all.forEach(d => {
      if (d !== detail && d.hasAttribute('open')) {
        this.cancel(d);
        this.closeImmediate(d);
      }
    });

    this.fadeIn(detail);
  }

  closeDelayed(detail) {
    this.hoverTimeout = setTimeout(() => {
      const menu = this.getMenu(detail);
      if (detail.matches(':hover') || menu?.matches(':hover')) return;
      this.fadeOut(detail);
    }, 120);
  }

  cancel(detail) {
    const anim = this.animations.get(detail);
    if (anim) {
      anim.cancel();
      this.animations.delete(detail);
    }
  }

  /* ---------- ANIMATIONS ---------- */

  fadeIn(detail) {
    const menu = this.getMenu(detail);
    if (!menu) return;

    this.removeHeaderTransparency();
    detail.setAttribute('open', '');

    menu.style.visibility = 'visible';
    menu.style.opacity = '0';

    const anim = menu.animate(
      { opacity: [0, 1] },
      { duration: 300, easing: 'ease-out', fill: 'forwards' }
    );

    this.animations.set(detail, anim);
    anim.onfinish = () => this.animations.delete(detail);
  }

  fadeOut(detail) {
    const menu = this.getMenu(detail);
    if (!menu) return this.closeImmediate(detail);

    const anim = menu.animate(
      { opacity: [1, 0] },
      { duration: 200, easing: 'ease-in', fill: 'forwards' }
    );

    this.animations.set(detail, anim);
    anim.onfinish = () => {
      this.closeImmediate(detail);
      this.animations.delete(detail);
    };
  }

  closeImmediate(detail) {
    const menu = this.getMenu(detail);

    if (menu) {
      menu.style.opacity = '0';
      menu.style.visibility = 'hidden';
    }

    detail.removeAttribute('open');
    this.setHeaderTransparentIfAllowed();
  }
}

customElements.define('nav-megamenu', NavMegamenu);
